import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  CheckCircle2, AlertCircle, RefreshCw, Trash2, Wallet, ExternalLink,
  Sparkles, ShieldCheck,
} from "lucide-react";

const PAYPAL_DEV = "https://developer.paypal.com/dashboard/applications/sandbox";

export default function PayPalConnectCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [form, setForm] = useState({
    env: "sandbox", client_id: "", client_secret: "", webhook_id: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/paypal/status");
      setStatus(data);
    } catch (err) {
      console.warn("[PayPal] status load failed", err?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const connect = async (e) => {
    e?.preventDefault?.();
    if (!form.client_id || !form.client_secret) return;
    setLoading(true);
    try {
      const { data } = await api.post("/paypal/connect", form);
      setStatus(data);
      setDlgOpen(false);
      setForm({ env: "sandbox", client_id: "", client_secret: "", webhook_id: "" });
      toast.success("PayPal connected");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't connect PayPal");
    } finally {
      setLoading(false);
    }
  };

  const disconnect = async () => {
    setLoading(true);
    try {
      await api.delete("/paypal/connect");
      setStatus({ connected: false });
      toast.success("PayPal disconnected");
    } catch (err) {
      toast.error("Couldn't disconnect");
    } finally {
      setLoading(false);
      setConfirmDelete(false);
    }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/paypal/sync-transactions", null, { params: { days: 30 } });
      const msg = `${data.imported} imported · ${data.skipped} skipped (${data.total} scanned)`;
      toast.success(`PayPal synced · ${msg}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const connected = status && status.has_secret;

  return (
    <div className="card-flat p-5" data-testid="paypal-connect-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#003087] flex items-center justify-center text-white font-bold text-sm shrink-0">
            PP
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-[var(--muted-text)]">
              PayPal
            </div>
            <div className="font-display text-xl">
              {connected ? "Connected" : "Connect PayPal"}
            </div>
            {connected ? (
              <div className="text-xs text-[var(--muted-text)] mt-1 flex items-center gap-2">
                <ShieldCheck className="w-3 h-3 text-green-700" />
                {status.env === "sandbox" ? "Sandbox mode" : "Live mode"}
                <span>·</span>
                <span className="font-mono-data">
                  {status.client_id?.slice(0, 8)}…{status.client_id?.slice(-4)}
                </span>
                {status.last_synced_at && (
                  <>
                    <span>·</span>
                    <span>synced {new Date(status.last_synced_at).toLocaleString()}</span>
                  </>
                )}
              </div>
            ) : (
              <p className="text-sm text-[var(--muted-text)] mt-1 max-w-md">
                Accept payments on invoices, import PayPal transactions, and
                pay vendors — all from Accountrix. Sandbox first, live later.
              </p>
            )}
          </div>
        </div>

        {connected ? (
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="outline"
              onClick={sync}
              disabled={syncing}
              data-testid="paypal-sync-btn"
              className="rounded-full h-9"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Syncing…" : "Sync"}
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setConfirmDelete(true)}
              data-testid="paypal-disconnect-btn"
              title="Disconnect PayPal"
              className="text-[#B4283A] hover:bg-[#FCE7E9]"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="paypal-connect-btn"
                className="btn-brand rounded-full h-9"
                size="sm"
              >
                <Sparkles className="w-3.5 h-3.5 mr-1" /> Connect
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="paypal-connect-dialog">
              <DialogHeader>
                <DialogTitle>Connect PayPal</DialogTitle>
                <DialogDescription>
                  Paste your PayPal REST API credentials. We store the secret
                  encrypted at rest and never share it with third parties.
                </DialogDescription>
              </DialogHeader>

              <div className="bg-[var(--brand-light)] border border-[var(--brand)]/30 rounded-md p-3 text-xs space-y-1">
                <div className="font-medium text-[var(--brand-text)] flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  Need credentials?
                </div>
                <div className="text-[var(--muted-text)]">
                  Go to{" "}
                  <a
                    href={PAYPAL_DEV}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[var(--brand-text)] underline inline-flex items-center gap-0.5"
                  >
                    developer.paypal.com <ExternalLink className="w-2.5 h-2.5" />
                  </a>{" "}
                  → Apps &amp; Credentials → Sandbox → Create App. Copy the
                  Client ID and Secret from that app.
                </div>
              </div>

              <form onSubmit={connect} className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-wide text-[var(--muted-text)]">
                    Environment
                  </Label>
                  <Select value={form.env} onValueChange={(v) => setForm({ ...form, env: v })}>
                    <SelectTrigger data-testid="paypal-env" className="mt-1 h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sandbox">Sandbox (test money)</SelectItem>
                      <SelectItem value="live">Live (real money)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-[var(--muted-text)]">
                    Client ID
                  </Label>
                  <Input
                    data-testid="paypal-client-id"
                    value={form.client_id}
                    onChange={(e) => setForm({ ...form, client_id: e.target.value.trim() })}
                    placeholder="AY... (starts with A)"
                    className="mt-1 h-10 font-mono-data text-xs"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-[var(--muted-text)]">
                    Client Secret
                  </Label>
                  <Input
                    data-testid="paypal-client-secret"
                    type="password"
                    value={form.client_secret}
                    onChange={(e) => setForm({ ...form, client_secret: e.target.value.trim() })}
                    placeholder="EL..."
                    className="mt-1 h-10 font-mono-data text-xs"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-[var(--muted-text)]">
                    Webhook ID (optional)
                  </Label>
                  <Input
                    data-testid="paypal-webhook-id"
                    value={form.webhook_id}
                    onChange={(e) => setForm({ ...form, webhook_id: e.target.value.trim() })}
                    placeholder="Only if you've set up a webhook already"
                    className="mt-1 h-10 font-mono-data text-xs"
                  />
                  <div className="text-[10px] text-[var(--muted-text)] mt-1">
                    Point your PayPal webhook at:{" "}
                    <span className="font-mono-data">
                      {window.location.origin.replace(":3000", "")}/api/paypal/webhook
                    </span>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDlgOpen(false)}
                    data-testid="paypal-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="btn-brand"
                    disabled={loading || !form.client_id || !form.client_secret}
                    data-testid="paypal-submit"
                  >
                    {loading ? "Verifying…" : "Connect"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Features hint */}
      {!connected && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-4 text-xs">
          {[
            { icon: <CheckCircle2 className="w-4 h-4 text-green-700" />, label: "Invoice Pay Now" },
            { icon: <Wallet className="w-4 h-4 text-[#003087]" />, label: "Transaction import" },
            { icon: <Sparkles className="w-4 h-4 text-[#E07A5F]" />, label: "Vendor payouts" },
          ].map((f) => (
            <div key={f.label} className="flex items-center gap-2 text-[var(--muted-text)]">
              {f.icon}
              <span>{f.label}</span>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect PayPal?</AlertDialogTitle>
            <AlertDialogDescription>
              Removes the stored credentials. Previously imported PayPal transactions and captured invoice payments stay on your books.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={disconnect}
              className="bg-[#B4283A] hover:bg-[#8f1f2e] text-white"
              data-testid="paypal-disconnect-confirm"
            >
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
